import { reactive, watch } from "vue";
import { useDirtyState as useDirtyStateCore } from "@eudonet/opal";
/**
 * Profil bloc form logic (OP-5862): local state + dirty tracking. All inputs are
 * closed selects so there is no field-level validation — the backend validator
 * is the defense-in-depth layer.
 *
 * @param contact The loaded contact aggregate. The form re-populates and
 *   re-baselines whenever we switch to a different contact. A same-contact
 *   refetch (e.g. another bloc's save triggers a page-level reload) does NOT
 *   clobber in-progress edits.
 */
export function useContactProfileForm(contact) {
    const form = reactive({
        status: "",
        categories: [],
        source: "",
        motivation: "",
        relationLevel: "",
        contactFrequency: "",
        preferredChannel: "",
    });
    // `enableBeforeUnload: false` — the page-level orchestrator owns the single
    // `beforeunload` listener covering every registered bloc.
    const { isDirty, markAsSaved: markDirtyStateSaved, resetToOriginal } = useDirtyStateCore({
        data: form,
        enableBeforeUnload: false,
    });
    // Status baseline, tracked independently of the dirty state so the merged PUT
    // omits status unless the user actually changed it here. OP-5862 removed status
    // from the generic Details update precisely to avoid the lost-update hazard:
    // status has an out-of-band mutation path, so re-sending the loaded value on a
    // save that only touched, say, firstName would clobber a concurrent status
    // change. `UpdateContactPayload.status` is omit-to-preserve, so leaving it out
    // when untouched is the correct no-op. (OP-6070 re-introduced the field via this
    // merged PUT; this guard keeps that compatible with the lost-update constraint.)
    let baselineStatus = "";
    /**
     * Returns the profile fragment of the merged PUT payload (OP-5862 — the page
     * issues ONE update covering Identity + Profile). `status` is sent ONLY when it
     * diverges from the loaded baseline so an untouched status never overwrites a
     * concurrently-changed value.
     */
    function buildPayload() {
        return {
            status: form.status !== baselineStatus ? form.status || undefined : undefined,
            categories: [...form.categories],
            source: form.source || undefined,
            motivation: form.motivation || undefined,
            relationLevel: form.relationLevel || undefined,
            contactFrequency: form.contactFrequency || undefined,
            preferredChannel: form.preferredChannel || undefined,
        };
    }
    // Populate + re-baseline when the contact resolves or we switch to a different
    // contact. A *same-contact* re-fetch (e.g. another bloc's save() triggers a
    // page-level reload of the shared aggregate) MUST NOT clobber this bloc's
    // in-progress edits. Re-initialise only on a contact change, or when this
    // bloc is clean (nothing unsaved to lose).
    let lastContactId = null;
    watch(contact, (loaded) => {
        if (!loaded)
            return;
        const sameContact = loaded.id === lastContactId;
        if (sameContact && isDirty.value)
            return;
        lastContactId = loaded.id;
        form.status = loaded.status ?? "";
        baselineStatus = form.status;
        form.categories = [...loaded.categories];
        form.source = loaded.source ?? "";
        form.motivation = loaded.motivation ?? "";
        form.relationLevel = loaded.relationLevel ?? "";
        form.contactFrequency = loaded.contactFrequency ?? "";
        form.preferredChannel = loaded.preferredChannel ?? "";
        markDirtyStateSaved();
    }, { immediate: true });
    // Re-baseline status alongside the dirty tracker so a subsequent save touching
    // only another field no longer re-emits the just-persisted status.
    function markAsSaved() {
        baselineStatus = form.status;
        markDirtyStateSaved();
    }
    return { form, isDirty, buildPayload, markAsSaved, resetToOriginal };
}
